import React, { useState } from 'react';
import { View, TouchableOpacity, TextInput, ScrollView, ActivityIndicator } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { translate } from '../../services/translate';
import MyAppText from '../MyAppText';

export interface FilterListItem<T = number> {
    label: string;
    value: T;
    color?: string;
}

interface Props<T> {
    items: FilterListItem<T>[];
    value: T;
    setValue: React.Dispatch<React.SetStateAction<T>>;
    clearValue: T;
    hideClearValue?: boolean;
    renderBadge?: boolean;
    label?: string;
    isLoading?: boolean;
    disabled?: boolean;
}

function FilterRadioList<T extends string | number>({ items, value, setValue, clearValue, renderBadge, label, isLoading, disabled = false, hideClearValue }: Props<T>) {
    const { styles, theme } = useStyles(styleSheet);
    const [search, setSearch] = useState('');

    const filtered = items.filter(item =>
        item.label.toLowerCase().includes(search.toLowerCase())
    );

    const isEmpty = value === clearValue;

    return (
        <View style={[styles.container, disabled && styles.containerDisabled]}>
            {label && <MyAppText style={styles.filterLabel}>{label}</MyAppText>}

            {items.length >= 7 && (
                <View style={styles.searchContainer}>
                    <FontAwesomeIcon icon={faMagnifyingGlass as IconDefinition} fontSize={16} color={theme.colors.neutral['300']} />
                    <TextInput
                        style={styles.searchInput}
                        placeholder={translate('typeToSearch')}
                        placeholderTextColor={theme.colors.neutral['300']}
                        value={search}
                        onChangeText={setSearch}
                        editable={!disabled}
                    />
                </View>
            )}

            <View style={styles.countRow}>
                <MyAppText style={styles.countText}>
                    {translate('items')}: {filtered.length}
                </MyAppText>
                {!hideClearValue &&
                    <TouchableOpacity onPress={() => setValue(clearValue)} disabled={isEmpty || disabled}>
                        <MyAppText style={[styles.clearSelection, (isEmpty || disabled) && styles.clearSelectionDisabled]}>
                            {translate('clearSelection')}
                        </MyAppText>
                    </TouchableOpacity>
                }
            </View>

            {isLoading ? (
                <View style={styles.loadingContainer}>
                    <ActivityIndicator size='small' color={theme.colors.primary['500']} />
                </View>
            ) : (
                <ScrollView
                    style={styles.list}
                    contentContainerStyle={{ gap: 8 }}
                    nestedScrollEnabled
                >
                    {filtered.map((item) => {
                        const isSelected = value === item.value;
                        return (
                            <TouchableOpacity
                                key={String(item.value)}
                                style={styles.row}
                                onPress={() => setValue(isSelected ? clearValue : item.value)}
                                disabled={disabled}
                            >
                                <View style={[styles.radio, isSelected && styles.radioSelected]}>
                                    {isSelected && <View style={styles.radioInner} />}
                                </View>
                                {renderBadge ? (
                                    <View style={[styles.badge, { backgroundColor: item.color ?? theme.colors.neutral['300'] }]}>
                                        <MyAppText style={styles.badgeText}>{item.label}</MyAppText>
                                    </View>
                                ) : (
                                    <MyAppText style={styles.label}>{item.label}</MyAppText>
                                )}
                            </TouchableOpacity>
                        );
                    })}
                </ScrollView>
            )}
        </View>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: { gap: 12 },
    containerDisabled: {
        opacity: 0.5,
    },
    filterLabel: {
        fontSize: 16,
        fontWeight: '600',
        color: theme.colors.labelColor,
    },
    searchContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        borderWidth: 1,
        borderColor: theme.colors.neutral['300'],
        borderRadius: 8,
        paddingHorizontal: 12,
        paddingVertical: 10,
        gap: 8,
    },
    searchInput: {
        flex: 1,
        fontSize: 16,
        color: theme.colors.neutral['400'],
        outlineStyle: 'none',
    },
    countRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    countText: {
        fontSize: 14,
        fontWeight: 'bold',
        color: theme.colors.labelColor,
    },
    clearSelection: {
        fontSize: 14,
        fontWeight: 'bold',
        color: theme.colors.primary['500'],
    },
    clearSelectionDisabled: {
        opacity: 0.4,
    },
    loadingContainer: {
        paddingVertical: 20,
        alignItems: 'center',
        justifyContent: 'center',
    },
    list: {
        maxHeight: 208,
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    radio: {
        width: 20,
        height: 20,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: theme.colors.neutral['300'],
        alignItems: 'center',
        justifyContent: 'center',
    },
    radioSelected: {
        borderColor: theme.colors.primary['500'],
    },
    radioInner: {
        width: 10,
        height: 10,
        borderRadius: 5,
        backgroundColor: theme.colors.primary['500'],
    },
    badge: {
        paddingHorizontal: 12,
        paddingVertical: 4,
        borderRadius: 20,
    },
    badgeText: {
        fontSize: 14,
        fontWeight: '600',
    },
    label: {
        fontSize: 16,
        color: theme.colors.labelColor,
    },
}));

export default FilterRadioList;
